import isEqual from 'lodash.isequal'
import { HslColor } from 'react-colorful'
import { ThemeColor } from '../common/models/colors'
import { ITheme } from '../context/AppContext'

// Define all colors for the project in HSL format - all other colors are calculated from these:
export const baseColors = {
  // base
  text: [252, 42, 28],
  primary: [256, 88, 64],
  secondary: [0, 96, 64],
  // values
  tertiary: [208, 96, 64],
  quaternary: [252, 42, 48],
  quinary: [0, 96, 64],
  selected: [48, 92, 64],
  alternate: [92, 56, 64],
  warning: [24, 75, 35],
  // white & black
  lightest: [0, 0, 100],
  darkest: [0, 0, 0],
}

/**
 * Starter brand palette for a freshly created company, so the (required)
 * companyColors are never empty; the admin tweaks these in BrandingSettings.
 * Stored as [h, s, l] triples (not rgb), matching BrandingSettings.
 *
 * This is the single source for "an untouched palette": both the company seed
 * (createCompany.script.ts) and the `branding` setup task (companySetup.ts,
 * which checks whether `text` still equals the default) read it from here.
 * Defining it twice made a brand-new company count as already branded.
 */
export const DEFAULT_COMPANY_COLORS: { [key: string]: number[] } = {
  text: baseColors.text,
  primary: baseColors.primary,
  // A slightly warmer, deeper red than baseColors.secondary — the starter accent.
  secondary: [4, 90, 58],
  // Mid-tone grey (= textLight @ lum 48), the nav's original fallback. NOT white
  // ([0,0,100]) — the nav renders light foreground on this, so white washes it out.
  navbg: [0, 0, 48],
}

/**
 * Default palette for culture values that don't have an admin-picked colour yet
 * (e.g. AI-generated proposals). Assigned by index so a fresh set of values
 * lands in distinct, friendly colours. Stored as [h, s, l] triples — the same
 * shape as companyColors entries.
 */
export const DEFAULT_CULTURE_COLORS: number[][] = [
  [257, 86, 63], // violet
  [196, 53, 57], // teal
  [154, 39, 69], // mint
  [37, 67, 68], // amber
  [23, 71, 66], // coral
]

/**
 * The Nth default culture colour, cycling the palette so any count is covered.
 */
export const defaultCultureColor = (index: number): number[] => {
  const length = DEFAULT_CULTURE_COLORS.length
  return DEFAULT_CULTURE_COLORS[((index % length) + length) % length]
}

/**
 * Some components allow color customization. Since that's
 * optional, use this as a default fallback color in case
 * it's unset
 */
export const defaultColor: ThemeColor = 'primary'

export const defaultTextColor: ThemeColor = 'text'

/**
 * Calculate a color from a 3-number JS Array to a CSS hsl() function string
 * @param hslArray An array of 3 numbers representing hue, saturation & luminosity
 * @param changeLuminosityTo Optionally, change luminosity to a given value (0-100)
 * @returns eg. "hsl(45, 45, 45)"
 */
export const createColor = (
  hslArray: number[],
  changeLuminosityTo?: number
) => {
  // Guard against a missing/invalid triple (e.g. a culture value whose colour token
  // isn't in companyColors) so callers can't crash the page; fall back to a neutral.
  if (!Array.isArray(hslArray) || hslArray.length < 3) {
    return `hsl(0, 0%, ${
      changeLuminosityTo !== undefined ? changeLuminosityTo : 100
    }%)`
  }
  return `hsl(${hslArray[0]}, ${hslArray[1]}%, ${
    changeLuminosityTo !== undefined ? changeLuminosityTo : hslArray[2]
  }%)`
}

/**
 * Convert an [h, s, l] triple to react-colorful's HslColor object.
 * Company colours are stored as such triples (see companyColors).
 * @returns undefined when the input isn't a valid 3-number array
 */
export const toHsl = (color?: number[]): HslColor | undefined =>
  color?.length === 3 ? { h: color[0], s: color[1], l: color[2] } : undefined

/**
 * Convert a HSL color string to an array of 3 numbers
 * @param hslString An HSL color string in the format "hsl(45, 45%, 45%)"
 * @returns An array of 3 numbers representing hue, saturation & luminosity
 */
export const hslToArray = (hslString) => {
  const regex = /hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/
  const match = hslString.match(regex)

  if (match) {
    return match.slice(1).map(Number)
  }

  throw new Error('Invalid HSL string format')
}

// Tints a "#rrggbb" token to an rgba() string
export const hexToRgba = (hex: string, alpha: number): string => {
  const value = parseInt(hex.replace('#', ''), 16)
  if (Number.isNaN(value)) return hex
  // eslint-disable-next-line no-bitwise
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${
    // eslint-disable-next-line no-bitwise
    value & 255
  }, ${Math.min(1, Math.max(0, alpha)).toFixed(2)})`
}

/**
 * Create a HSLA color string including opacity from one of the existing colors
 * param colorKey Key of an item from the "colors" object, eg. "primary"
 * @param opacity Opacity to apply (range: 0-1), eg. 0.6
 * @returns eg. "hsla(45, 45, 45)"
 */
export const hsla = (color: string, opacity: number) => {
  const hslValues = color.substring(4, color.indexOf(')'))
  return `hsla(${hslValues}, ${opacity})`
}

export const hslToHex = (hslaString: string): string => {
  // Normalize input (handle both HSL/hslaString and inconsistent commas/spaces)
  const normalized = hslaString
    .replace(/hsla?\(/, '')
    .replace(/\)/, '')
    .replace(/%/g, '')
    .replace(/\s+/g, ',')
    .split(',')
    .filter((val) => val !== '')

  // Extract components
  const h = parseFloat(normalized[0])
  const s = parseFloat(normalized[1]) / 100
  const l = parseFloat(normalized[2]) / 100
  const a = normalized[3] ? parseFloat(normalized[3]) : undefined

  // HSL to RGB conversion (correct math)
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2

  let r: number
  let g: number
  let b: number
  if (h < 60) [r, g, b] = [c, x, 0]
  else if (h < 120) [r, g, b] = [x, c, 0]
  else if (h < 180) [r, g, b] = [0, c, x]
  else if (h < 240) [r, g, b] = [0, x, c]
  else if (h < 300) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]

  // Convert RGB to Hex
  const toHex = (val: number) =>
    Math.round((val + m) * 255)
      .toString(16)
      .padStart(2, '0')
  const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`

  // Add alpha if provided (as 2-digit hex)
  if (a !== undefined) {
    const alphaHex = Math.round(a * 255)
      .toString(16)
      .padStart(2, '0')
    return `${hex}${alphaHex}`
  }
  return hex
}

export const hslThemeToHex = (theme: ITheme): ITheme => {
  const themeHex = {
    ...theme,
    colors: Object.entries(theme.colors).reduce((acc, [key, value]) => {
      acc[key] = hslToHex(value) // Convert HSL to Hex and assign to key
      return acc
    }, {} as Record<string, string>), // Preserve the object structure
  }
  return themeHex as ITheme
}

export const hslToRGB = (hsl: string) => {
  const sep = hsl.indexOf(',') > -1 ? ',' : ' '
  const hslColor: any = hsl.substring(4).split(')')[0].split(sep)

  const h = hslColor[0]
  const s = hslColor[1].substring(0, hslColor[1].length - 1) / 100
  const l = hslColor[2].substring(0, hslColor[2].length - 1) / 100

  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let r = 0
  let g = 0
  let b = 0

  if (0 <= h && h < 60) {
    r = c
    g = x
    b = 0
  } else if (60 <= h && h < 120) {
    r = x
    g = c
    b = 0
  } else if (120 <= h && h < 180) {
    r = 0
    g = c
    b = x
  } else if (180 <= h && h < 240) {
    r = 0
    g = x
    b = c
  } else if (240 <= h && h < 300) {
    r = x
    g = 0
    b = c
  } else if (300 <= h && h < 360) {
    r = c
    g = 0
    b = x
  }
  r = Math.round((r + m) * 255)
  g = Math.round((g + m) * 255)
  b = Math.round((b + m) * 255)

  return {
    r,
    g,
    b,
    rgb: 'rgb(' + r + ',' + g + ',' + b + ')',
  }
}

// YIQ brightness at which a background stops being "dark" and the foreground
// flips to the dark colour. The textbook value is 128; 150 already leans towards
// the light foreground, which suits the saturated brand tints companies pick.
export const CONTRAST_THRESHOLD = 150

// The public employer-branding pages run a higher bar on purpose: a row of
// culture values is usually one hue in several lightnesses, and a set that
// straddles 150 renders half its cards white-on-colour and half black-on-colour.
// Holding the light foreground up to 175 keeps such a set consistent. It is a
// deliberate brand choice over the contrast norm — white on a tint this light
// lands near 2.5:1, under WCAG AA — so keep it to the public pages, and note
// that yellows (YIQ 190+) still flip to dark, where white is simply unreadable.
export const PUBLIC_CONTRAST_THRESHOLD = 175

/**
 * Pick a legible foreground colour for an arbitrary background tint: returns the
 * dark colour on light backgrounds and the light colour on dark ones, based on
 * the background's perceived (YIQ) brightness. Falls back to the light colour
 * when the background isn't a valid `hsl(...)` string.
 * @param background An `hsl(...)` colour string (e.g. a user-picked tint)
 * @param darkColor Foreground to use on light backgrounds (defaults to the `darkest` theme token)
 * @param lightColor Foreground to use on dark backgrounds (defaults to the `lightest` theme token)
 * @param threshold Brightness at which to flip to `darkColor` (see `PUBLIC_CONTRAST_THRESHOLD`)
 */
export const contrastColorOn = (
  background: string,
  darkColor = 'darkest',
  lightColor = 'lightest',
  threshold = CONTRAST_THRESHOLD
): string => {
  if (!background || !background.includes('hsl')) return lightColor
  const { r, g, b } = hslToRGB(background)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  return brightness >= threshold ? darkColor : lightColor
}

/**
 * Convert a hex color string to its red, green & blue channels
 * @param hex eg. "#5B4FE0" (with or without leading '#')
 * @returns { r, g, b } or null when the string isn't a valid 6-digit hex
 */
export const hexToRgb = (
  hex: string
): { r: number; g: number; b: number } | null => {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim())
  if (!match) return null
  return {
    r: parseInt(match[1], 16),
    g: parseInt(match[2], 16),
    b: parseInt(match[3], 16),
  }
}

/**
 * Convert red, green & blue channels (0-255) to a HSL array
 * @returns An array of 3 numbers representing hue, saturation & luminosity
 */
export const rgbToHsl = (r: number, g: number, b: number): number[] => {
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0
  let s = 0

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      default:
        h = (r - g) / d + 4
    }
    h *= 60
  }

  return [Math.round(h), Math.round(s * 100), Math.round(l * 100)]
}

export const createColors = (
  baseColorSet: { [key: string]: number[] },
  overridePrimary?: boolean
) => {
  const baseColorSetConverted: object = {}
  Object.keys(baseColorSet).forEach(
    (key) => (baseColorSetConverted[key] = createColor(baseColorSet[key]))
  )

  return {
    ...baseColorSetConverted,
    text: createColor(baseColorSet.text),
    primary: createColor(baseColorSet.primary),
    secondary: createColor(baseColorSet.secondary),
    tertiary: createColor(baseColorSet.tertiary),
    selected: createColor(baseColorSet.selected),
    alternate: createColor(baseColorSet.alternate),
    quaternary: createColor(baseColorSet.quaternary),
    quinary: createColor(baseColorSet.quinary),
    darkest: createColor(baseColorSet.darkest),
    lightest: createColor(baseColorSet.lightest),
    // Calculated from the "darkest" color:
    dark: createColor(baseColorSet.darkest, 48),
    darker: createColor(baseColorSet.darkest, 12),
    // Calculated from the "lightest" color:
    light: createColor(baseColorSet.lightest, 84),
    lighter: createColor(baseColorSet.lightest, 92),
    // Calculated from the "text" color:
    textFaint: createColor(baseColorSet.text, 98),
    textLightest: createColor(baseColorSet.text, 95),
    textLighter: createColor(baseColorSet.text, 84),
    text72: createColor(baseColorSet.text, 72),
    textLight: createColor(baseColorSet.text, 48),
    textDark: createColor(baseColorSet.text, 15),
    // Calculated from the "primary" color:
    primaryLight: createColor(
      overridePrimary ? baseColorSet.text : baseColorSet.primary,
      98
    ),
    primaryMild: createColor(
      overridePrimary ? baseColorSet.text : baseColorSet.primary,
      74
    ),
    primaryDark: createColor(
      overridePrimary ? baseColorSet.text : baseColorSet.primary,
      28
    ),
    // Faded primary colors
    primaryFadeLight: hsla(createColor(baseColorSet.primary), 0.15),
    primaryFadeHalf: hsla(createColor(baseColorSet.primary), 0.5),
    primaryFadeSemi: hsla(createColor(baseColorSet.primary), 0.8),
    // Calculated from the "secondary" color:
    secondaryLight: createColor(baseColorSet.secondary, 98),
    secondaryFade: createColor(baseColorSet.secondary, 94),
    secondaryDark: createColor(baseColorSet.secondary, 40),
    // Calculated from the "tertiary" color:
    tertiaryLight: createColor(baseColorSet.tertiary, 93),
    tertiaryDark: createColor(baseColorSet.tertiary, 40),
    // Calculated from the "selected" color:
    selectedLight: createColor(baseColorSet.selected, 92),
    selectedDark: createColor(baseColorSet.selected, 40),
    // Calculated from the "alternate" color:
    alternateLight: createColor(baseColorSet.alternate, 96),
    alternateDark: createColor(baseColorSet.alternate, 40),
    // Calculated from the "warning" color:
    warning: createColor(baseColorSet.warning),
    warningLight: createColor(baseColorSet.warning, 94),
    // Calculated from the "quaternary" color:
    quaternaryLight: createColor(baseColorSet.quaternary, 98),
    // Calculated from the "quinary" color:
    quinaryLight: createColor(baseColorSet.quinary, 98),
  }
}

// Builds the app theme from a company's brand colours, falling back to baseColors.
// Customisation (e.g. overridePrimary) is measured against the canonical
// baseColors, never against an already-themed state.
export const createCompanyTheme = (companyColors?: {
  [key: string]: number[]
}): ITheme => {
  companyColors ??= {}
  const newColors = {
    ...companyColors,
    text: companyColors.text || baseColors.text,
    primary: companyColors.primary || baseColors.primary,
    secondary: companyColors.secondary || baseColors.secondary,
    // values
    tertiary: companyColors.tertiary || baseColors.tertiary,
    quaternary: companyColors.quaternary || baseColors.quaternary,
    quinary: companyColors.quinary || baseColors.quinary,
    selected: companyColors.selected || baseColors.selected,
    alternate: companyColors.alternate || baseColors.alternate,
    warning: companyColors.warning || baseColors.warning,
    // white & black,
    lightest: baseColors.lightest,
    darkest: baseColors.darkest,
  }
  return {
    id: 1,
    baseColors: newColors,
    colors: createColors(newColors, !isEqual(newColors.text, baseColors.text)),
  }
}

export const createHexThemeFromCompanyColors = (companyColors?: {
  [key: string]: number[]
}): ITheme => hslThemeToHex(createCompanyTheme(companyColors))

// SUMM's own colours, never a company's: the SUMM logo wears these wherever it
// appears, so a company's branding never recolours it
export const summColors = createColors(baseColors)
